import { BEIGE_300, BEIGE_600, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, MenuItem, MenuItemProps, SxProps, Theme, Typography, menuItemClasses, styled } from '@mui/material'
import React, { ReactElement, ReactNode, useContext } from 'react'

import { MUIDropdownKeyboardContext } from '../MUIDropdown/MUIDropdownKeyboard.context'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Dropdown item title */
  title?: string,
  /** Whether the dropdown item is disabled or not */
  disabled?: boolean,
  /** Whether the dropdown item is selected or not */
  isSelected?: boolean,
  /** OnClick action on dropdown item */
  onClick?: () => void
  children?: ReactNode
  /** Custom styles on dropdown item */
  sx?: SxProps<Theme>
  /** MenuItem props; `{ component: 'a', href }` makes a link item */
  props?: MenuItemProps & Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'>
  /** Leading icon, 2rem, GRAY_700 (CORAL_600 when `isDestructive`) */
  icon?: ReactElement
  /** A destructive action ("End campaign"): title and icon in CORAL_600 */
  isDestructive?: boolean
  /** Set by MUI's MenuList on the item that takes focus; forwarded only inside a keyboard-navigable MUIDropdown */
  autoFocus?: boolean
  /** Set by MUI's MenuList to put one item in the tab order; forwarded only inside a keyboard-navigable MUIDropdown */
  tabIndex?: number
}

const StyledMenuItem = styled(MenuItem)({
  [`&.${menuItemClasses.selected}`]: {
    backgroundColor: BEIGE_600,
    ':hover': {
      backgroundColor: BEIGE_600
    }
  }
})

/**
 * @component MUI Dropdown item
 * @example
 * <MUIDropdownItem title="Deploy!" disabled={false} isSelected={true} />
 */
export const MUIDropdownItem: React.FC<Props> = ({
  title,
  disabled = false,
  isSelected,
  onClick,
  children,
  sx,
  props,
  icon,
  isDestructive = false,
  autoFocus,
  tabIndex,
}) => {
  const isKeyboardNavigable = useContext(MUIDropdownKeyboardContext)

  return (
    <StyledMenuItem
      disabled={disabled}
      selected={isSelected}
      onClick={onClick}
      {...(isKeyboardNavigable && { autoFocus, tabIndex })}
      sx={{
        padding: '1.2rem',
        ':hover': {
          backgroundColor: BEIGE_300
        },
        ...sx
      }}
      {...props}
    >
      {!!icon &&
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexShrink: 0,
            marginRight: '.8rem',
            color: isDestructive ? CORAL_600 : GRAY_700,
            '& svg': { fontSize: '2rem', width: '1em', height: '1em' },
          }}
        >
          {icon}
        </Box>
      }

      {title && <Typography variant="text-md" color={isDestructive ? CORAL_600 : GRAY_900}>{title}</Typography>}

      {children}
    </StyledMenuItem>
  )
}
